import { isString } from "@swiggy-private/js-utils";
import {
    SwgyHeadersList,
    SwgyHeadersKeys,
    SwgyHeaderKeysList,
    API_ANALYTICS_PLATFORM,
} from "@swiggy-private/checkout-types";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/payment-types";

export type Headers = { [header: string]: string | string[] | undefined };
export type SwgyHeaders = { [header: string]: string };
export function isWebPlatform(platform: string): boolean {
    const _platform = platform.toLowerCase();
    return _platform === "mweb" || _platform === "dweb";
}

export function isiOSPlatform(platform: string): boolean {
    const _platform = platform.toLowerCase();
    return _platform === "swiggy-ios" || _platform === "ios";
}

export function isAndroidPlatform(platform: string): boolean {
    const _platform = platform.toLowerCase();
    return _platform === "swiggy-android" || _platform === "an";
}

export const getMarketplaceBusinessLine = (headers: Headers): string => {
    const businessLine = headers["marketplacebusinessline"] as string;

    if (businessLine) return businessLine.toLowerCase();
    return MARKETPLACE_BUSINESS_LINE.REGULAR;
};

export const getMarketplaceCategory = (headers: Headers): string => {
    const businessCategory = headers["marketplacecategory"] as string;

    if (businessCategory) return businessCategory.toLowerCase();
    return "";
};

export const isInstamartFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const marketplaceCategory = getMarketplaceCategory(headers);
    return (
        marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DASH &&
        marketplaceCategory === MARKETPLACE_CATEGORY.INSTAMART
    );
};

export const isGiftingFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers)?.toLowerCase();
    const marketplaceCategory = getMarketplaceCategory(headers)?.toLowerCase();
    return (
        marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.GIFTING &&
        marketplaceCategory === MARKETPLACE_CATEGORY.GIFTING
    );
};

// For passing the required tenat config in juspay wallet api,
// "Tenant" is not part of SwgyHeaderList
export function getSwiggyHeaders(headers: Headers = {}): SwgyHeaders {
    const swgyHeaders: SwgyHeaders = {};
    Object.entries(headers).forEach(([key, value]) => {
        if (
            isString(value) &&
            value.length > 0 &&
            (SwgyHeadersList.indexOf(key.toLowerCase() as SwgyHeadersKeys) !== -1 || key.toLowerCase() === "tenant")
        ) {
            swgyHeaders[key] = value;
        }
    });

    if (swgyHeaders.lat) {
        swgyHeaders["latitude"] = swgyHeaders.lat;
    }

    if (swgyHeaders.lng) {
        swgyHeaders["longitude"] = swgyHeaders.lng;
    }

    // if m_id is present in headers passed, use that
    if (headers["m_id"]) {
        swgyHeaders["m_id"] = String(headers["m_id"]);
    } else {
        // else check for business line and category
        // [!IMPORTANT] Changing m_id only when bl is dash and category is IM
        // Do not change this for other cases without confirming with different BLS
        // as this might break for existing APIs
        swgyHeaders["m_id"] = MARKETPLACE_ID.CHECKOUT;
        if (isInstamartFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.DASH;
        } else if (isGiftingFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.GIFTING;
        }
    }

    if (headers["paymentamount"]) {
        swgyHeaders["paymentamount"] = String(headers["paymentamount"]);
    }

    swgyHeaders["x-client-id"] = "web-payment";

    if (isString(swgyHeaders.platform) && swgyHeaders.platform.length > 0) {
        swgyHeaders["client-id"] = swgyHeaders.platform;
    } else {
        swgyHeaders["client-id"] = "web-payment";
    }

    const platform = swgyHeaders[SwgyHeaderKeysList.platform] || "";
    switch (platform.toLowerCase()) {
        case "swiggy-android":
        case "an":
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.ANDROID;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.ANDROID;
            break;
        case "swiggy-ios":
        case "ios":
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.IOS;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.IOS;
            break;
        case "swiggy-dweb":
        case "dweb":
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.DWEB;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.DWEB;
            break;
        default:
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.MWEB;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.MWEB;
            break;
    }

    // We are passing actual ip-address from x-forwarded-for for all devices in headers for Simpl Eligibility call.
    // This is mandatory since Simpl will use this data for fraud detection and analysis.
    if (headers && headers["x-forwarded-for"] && typeof headers["x-forwarded-for"] === "string") {
        swgyHeaders["X-Forwarded-For"] = headers["x-forwarded-for"];
    }

    // We are passing actual user-agent for all devices in headers for Simpl Eligibility call.
    // This is mandatory since Simpl will use this user agent data for fraud detection and analysis.
    if (headers && headers["user-agent"] && typeof headers["user-agent"] === "string") {
        swgyHeaders["browser-user-agent"] = headers["user-agent"];
    }

    return swgyHeaders;
}
